import React, {
  createContext, useContext, useState, useMemo, useRef, useEffect
} from 'react';
import { node, bool } from 'prop-types';

import { isAppleTouch } from '../utils/promo-presentation-utils';
import { usePromoCart } from './PromoCartProvider';
import { usePromoModel } from './PromoModelProvider';

// note: these should be kept up to date with actual rendered heights
// this is used to configure padding for list when msb and has item in cart
const msbInfoContainerHeight = 80;
const bmsmInfoContainerHeight = 100;
const msbThumbnailBoxHeight = 63;
const msbGoToCartHeight = 55;
const bmsmGoToCartHeight = 74;
const bmsmExtraMessagingHeight = 22;

export const PromoPresentationContext = createContext();
export const usePromoPresentation = () => useContext(PromoPresentationContext);

export const PromoPresentationProvider = ({ isBatchLoading, renderPromoDrawer, children }) => {
  const { isMsb, isBmsm } = usePromoModel();
  const { promoCartQuantity, isPromoAchieved, currentPromoTier } = usePromoCart();

  const [promoListPadding, setPromoListPadding] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hideImages, setHideImages] = useState(false);
  const promoMsgContainerRef = useRef(null);
  const promoListRef = useRef(null);
  const isMounted = useRef(true);

  const isAppleTouchDevice = isAppleTouch();

  useEffect(() => {
    if (isMounted.current && (isMsb || isBmsm)) {
      const promoMsgContainerHeight = promoMsgContainerRef?.current?.getBoundingClientRect()?.height || 0;
      const infoContainerHeight = isMsb ? msbInfoContainerHeight : bmsmInfoContainerHeight;
      const initialMsbHeaderHeight = promoMsgContainerHeight + infoContainerHeight;
      const thumbnailHeight = promoCartQuantity ? msbThumbnailBoxHeight : 0;
      const msbCartHeight = isMsb && isPromoAchieved ? msbGoToCartHeight : 0;
      const bmsmCartHeight = isBmsm && isPromoAchieved ? bmsmGoToCartHeight : 0;
      const goToCartHeight = msbCartHeight || bmsmCartHeight;
      const bmsmMessagingHeight = (isBmsm && currentPromoTier > 0) ? bmsmExtraMessagingHeight : 0;
      const currentMaxHeight = initialMsbHeaderHeight + thumbnailHeight + goToCartHeight + bmsmMessagingHeight;
      setPromoListPadding(currentMaxHeight);
    }
  }, [renderPromoDrawer, promoCartQuantity, isPromoAchieved, isMsb, isBmsm, currentPromoTier]);

  useEffect(() => {
    setHideImages(false);
  }, [promoCartQuantity]);

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  const contextValue = useMemo(() => {
    return {
      hideImages,
      setHideImages,
      isDrawerOpen,
      setIsDrawerOpen,
      isBatchLoading,
      promoListPadding,
      isAppleTouchDevice,
      promoMsgContainerRef,
      promoListRef,
    };
  }, [
    hideImages,
    isDrawerOpen,
    isBatchLoading,
    promoListPadding,
    isAppleTouchDevice,
  ]);

  return (
    <PromoPresentationContext.Provider value={contextValue}>
      { children }
    </PromoPresentationContext.Provider>
  );
};

PromoPresentationProvider.propTypes = {
  isBatchLoading: bool.isRequired,
  children: node.isRequired,
  renderPromoDrawer: bool,
};

PromoPresentationProvider.defaultProps = {
  renderPromoDrawer: false,
};
